import { acquireToken } from './msal';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`api ${status}`);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await acquireToken();
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text);
  }
  return res.json() as Promise<T>;
}

// Returns null on 404 (e.g. user has no profile photo) instead of throwing,
// because callers tend to want a fall-through to a placeholder rather than an
// error banner. All other non-2xx responses still throw ApiError.
async function requestBlob(path: string): Promise<Blob | null> {
  const token = await acquireToken();
  const res = await fetch(`/api${path}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  // 204 No Content + legacy 404 both mean "no resource here" – return null
  // so callers can render an empty/avatar fallback without an exception.
  if (res.status === 204 || res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text);
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  getBlob: (path: string) => requestBlob(path),
};

// Shape of the JSON error bodies the backend returns (see the routers and the
// terminal error handler in app.ts). Every field is optional because different
// routes emit different subsets.
interface JsonErrorBody {
  error?: string;
  message?: string;
  issues?: Array<{ key: string; message: string }>;
  // A reference id echoed on 500s so an operator can quote it in a support
  // request (spec T54). The exact field name is being wired on the backend, so
  // accept the common spellings defensively.
  requestId?: string;
  reference?: string;
  referenceId?: string;
}

function referenceIdOf(j: JsonErrorBody): string | null {
  return j.requestId ?? j.reference ?? j.referenceId ?? null;
}

// The per-key validation issues from a 400, for callers that can render them
// against the field that caused them rather than only in a banner. Empty for
// any other error shape.
export function errorIssues(e: unknown): Array<{ key: string; message: string }> {
  if (!(e instanceof ApiError) || typeof e.body !== 'string') return [];
  try {
    const j = JSON.parse(e.body) as JsonErrorBody;
    return j.issues?.filter((i) => typeof i.key === 'string' && typeof i.message === 'string') ?? [];
  } catch {
    return [];
  }
}

// One JSON-aware error formatter for the whole app (spec T53/T54). Turns an
// ApiError into a human-readable line: a validation-issues list, a friendly
// { error, message } pair, or the raw status + body as a last resort. Any
// server-supplied reference id is appended so a 500 can be traced.
export function formatError(e: unknown): string {
  if (e instanceof ApiError) {
    if (typeof e.body === 'string') {
      try {
        const j = JSON.parse(e.body) as JsonErrorBody;
        const ref = referenceIdOf(j);
        const suffix = ref ? ` (ref: ${ref})` : '';
        if (j.issues?.length) {
          return `${e.status} ${j.error ?? 'invalid'}: ${j.issues
            .map((i) => `${i.key}: ${i.message}`)
            .join('; ')}${suffix}`;
        }
        if (j.message) return `${`${e.status} ${j.error ?? ''} ${j.message}`.trim()}${suffix}`;
        if (j.error) return `${e.status} ${j.error}${suffix}`;
        if (ref) return `${e.status} something went wrong${suffix}`;
      } catch {
        /* body isn't JSON – fall through to the raw text */
      }
    }
    return `${e.status} ${typeof e.body === 'string' ? e.body : JSON.stringify(e.body)}`;
  }
  return e instanceof Error ? e.message : 'unknown error';
}

// Downloads a protected endpoint: acquire a token, fetch with the Bearer header,
// turn the response into a blob, and click a synthetic <a download>. `path` is
// relative to /api. The filename comes from the Content-Disposition header,
// falling back to `fallbackName`. Throws ApiError on a non-2xx response so the
// caller can render its own loading/error state.
export async function downloadAuthenticated(path: string, fallbackName?: string): Promise<void> {
  const token = await acquireToken();
  const res = await fetch(`/api${path}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(dispo);
  const name = match?.[1] ?? fallbackName ?? 'download';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
